import React, { useState, useEffect } from 'react';
import { Plus, Search, Settings2, Activity, CheckCircle, Wrench, Power, PowerOff, Pencil, X, PauseCircle, Filter, ChevronsLeft } from 'lucide-react';
import { api } from '../lib/api';
import useBloqueioScroll from '../hooks/useBloqueioScroll';

// Junta a tabela local `maquinas` (status_operacional/horas_mensais são
// campos reais e editáveis aqui, ao contrário do proxy antigo que lia direto
// da SH1 e nunca tinha esses campos preenchidos).
//
// `ativo` (04/08/2026, pedido do usuário) — também campo só do Persona, sem fonte
// no Protheus (ST9, a candidata pra status/tipo de máquina, veio vazia em
// produção). Valor inicial veio de uma consulta real na SH6 (máquina sem
// nenhum apontamento nos últimos 3 anos entra inativa); dali em diante é
// editável na tela, sem recálculo automático.
//
// 18/08/2026 (pedido do usuário): removidos Localização e OEE. Localização
// nunca teve tela pra preencher (sempre vazia). OEE (GET /api/relatorios/oee,
// endpoint removido do backend junto com isso) precisa de 3 fatores —
// Disponibilidade sempre veio `null` (sem fonte real de janela planejada da
// OP no Protheus deles), então o card sempre mostrava "sem dado" pra toda
// máquina; sem uma fonte real de disponibilidade, o usuário preferiu tirar a
// métrica de vez a manter um indicador que nunca teria dado.
//
// 20/08/2026 (pedido do usuário): Tipo removido (nunca teve uso real na
// tela). "Capacidade" deixou de ter unidade de medida (kg/h, peças/h etc. —
// a ideia de 18/08/2026 foi abandonada, o campo ZM1_UNID pedido pro time de
// Basis não precisa mais ser criado) e virou "horas mensais disponíveis" —
// mesma coluna do banco (ZM1_CAPAC), só reaproveitada com um significado
// novo e único: quantas horas por mês a máquina PODE trabalhar. Base pro
// relatório futuro de utilização (horas disponíveis × horas realmente
// trabalhadas, via ZH6 — % de uso da máquina).
function mapMaquina(maquina) {
    return {
        id: maquina.id,
        codigo: maquina.codigo_protheus || '—',
        nome: maquina.nome,
        status: maquina.status_operacional || 'Aguardando',
        ativo: maquina.ativo !== false,
        horasMensaisValor: maquina.horas_mensais ?? null,
        horasMensais: maquina.horas_mensais != null
            ? `${Number(maquina.horas_mensais).toLocaleString('pt-BR')} h/mês`
            : '—',
    };
}

// 'Pausada' (27/08/2026, pedido do usuário: "preciso saber quando a máquina
// está pausado, em produção, aguardando") — status ao vivo de verdade agora
// (ZH6_STATUS mais recente do recurso, ver mapearStatusAoVivo/maquinas.js),
// não mais o campo estático ZM1_STATUS (que nunca era atualizado por nada).
// 'Manutenção'/'Setup' ficam só como fallback defensivo — nenhuma fonte real
// grava esses valores hoje, mas não custa manter o estilo caso apareçam.
const statusBadgeClass = {
    'Produzindo': 'mes-status--success',
    'Pausada': 'mes-status--warning',
    'Aguardando': 'mes-status--muted',
    'Manutenção': 'mes-status--danger',
    'Setup': 'mes-status--purple',
};

const statusIcon = {
    'Produzindo': <Activity size={13} />,
    'Pausada': <PauseCircle size={13} />,
    'Aguardando': <CheckCircle size={13} />,
    'Manutenção': <Wrench size={13} />,
    'Setup': <Settings2 size={13} />,
};

// Editar horas mensais disponíveis (18/08/2026, estendido 20/08/2026) — o
// backend (PUT /api/maquinas/:id) já aceitava isso desde sempre, mas
// nenhuma tela chamava. Mesmo padrão de modal simples já usado em
// GestaoUsuarios.jsx (sem portal — essa página já vive dentro do
// MainLayout normal, sem o problema de z-index que as telas do operador,
// sem sidebar, têm).
function ModalEditarMaquina({ maquina, onClose, onSalvar }) {
    useBloqueioScroll();
    const [horasMensais, setHorasMensais] = useState(maquina.horasMensaisValor ?? '');
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState('');

    const handleSalvar = async () => {
        setSalvando(true);
        setErro('');
        try {
            await onSalvar({
                horas_mensais: horasMensais === '' ? null : Number(horasMensais),
            });
        } catch (err) {
            setErro(err.message || 'Erro ao salvar máquina.');
            setSalvando(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
            <div className="mes-modal w-full max-w-md animate-scale-in">
                <div className="flex items-center justify-between p-6 border-b border-white/[0.04]">
                    <h2 className="text-base font-bold text-anthracite-100">Editar {maquina.codigo} - {maquina.nome}</h2>
                    <button onClick={onClose} className="p-2 rounded-xl hover:bg-white/[0.04] text-anthracite-500"><X size={16} /></button>
                </div>
                <div className="p-6 space-y-5">
                    <div>
                        <label className="block text-[11px] font-bold text-anthracite-400 uppercase tracking-[0.15em] mb-2">Horas mensais disponíveis</label>
                        <div className="flex items-center gap-2">
                            <input type="number" min="0" step="1" className="mes-input flex-1" value={horasMensais} onChange={(e) => setHorasMensais(e.target.value)} placeholder="Ex: 340" />
                            <span className="text-[12px] text-anthracite-500 shrink-0">horas/mês</span>
                        </div>
                        <p className="text-[11px] text-anthracite-500 mt-2">Quanto essa máquina pode trabalhar por mês — base pro relatório de utilização (horas disponíveis × horas realmente trabalhadas).</p>
                    </div>
                    {erro && <div className="rounded-2xl px-4 py-3 text-[12px] font-medium bg-red-500/10 border border-red-500/15 text-red-400">{erro}</div>}
                </div>
                <div className="flex items-center justify-end gap-3 p-6 border-t border-white/[0.04]">
                    <button onClick={onClose} disabled={salvando} className="mes-btn-ghost border border-white/[0.06] disabled:opacity-50">Cancelar</button>
                    <button onClick={handleSalvar} disabled={salvando} className="mes-btn-primary disabled:opacity-50">Salvar</button>
                </div>
            </div>
        </div>
    );
}

const Maquinarios = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filtroAtivo, setFiltroAtivo] = useState('ativas'); // 'ativas' | 'inativas' | 'todas' — Ativas é o padrão (pedido do usuário, 27/08/2026)
    // Filtro de status operacional (27/08/2026, pedido do usuário) — novo,
    // sobre o status AO VIVO (ver comentário em statusBadgeClass acima).
    const [filtroStatus, setFiltroStatus] = useState('Todos');
    // Quantidade + paginação (27/08/2026, pedido do usuário: "temos a base já
    // funcionando no menu de ordens de produção") — aqui é bem mais simples
    // que lá: a SH1 real tem menos de 200 recursos (não dezenas de milhares
    // de OPs com 20+ anos de histórico), então a lista inteira já carregada
    // de uma vez cabe numa página só de memória — "Página X de Y" exato é
    // instantâneo de calcular, sem nenhum dos problemas de performance que
    // exigiram o mecanismo de blocos em Ordens de Produção.
    const [quantidadeSelecionada, setQuantidadeSelecionada] = useState(50);
    const [paginaAtual, setPaginaAtual] = useState(1);
    const [maquinas, setMaquinas] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [erroApi, setErroApi] = useState('');
    const [editando, setEditando] = useState(null);

    useEffect(() => {
        api.get('/api/maquinas')
            .then((listaMaquinas) => setMaquinas(listaMaquinas.map(mapMaquina)))
            .catch((err) => setErroApi(err.message || 'Erro ao carregar máquinas.'))
            .finally(() => setCarregando(false));
    }, []);

    const handleToggleAtivo = async (maquina) => {
        const novoAtivo = !maquina.ativo;
        setMaquinas((atual) => atual.map((m) => (m.id === maquina.id ? { ...m, ativo: novoAtivo } : m)));
        try {
            await api.put(`/api/maquinas/${maquina.id}`, { ativo: novoAtivo });
        } catch (err) {
            setMaquinas((atual) => atual.map((m) => (m.id === maquina.id ? { ...m, ativo: maquina.ativo } : m)));
            setErroApi(err.message || 'Erro ao atualizar máquina.');
        }
    };

    const handleSalvarEdicao = async ({ horas_mensais }) => {
        const atualizada = await api.put(`/api/maquinas/${editando.id}`, { horas_mensais });
        setMaquinas((atual) => atual.map((m) => (m.id === editando.id ? mapMaquina(atualizada) : m)));
        setEditando(null);
    };

    const maquinasFiltradas = maquinas
        .filter((m) => filtroAtivo === 'todas' || (filtroAtivo === 'ativas' ? m.ativo : !m.ativo))
        .filter((m) => filtroStatus === 'Todos' || m.status === filtroStatus)
        .filter((m) =>
            m.codigo.toLowerCase().includes(searchTerm.toLowerCase()) ||
            m.nome.toLowerCase().includes(searchTerm.toLowerCase())
        );

    // Paginação — tudo em memória, dado o volume pequeno (ver comentário na
    // declaração de quantidadeSelecionada acima).
    const totalPaginas = Math.max(1, Math.ceil(maquinasFiltradas.length / quantidadeSelecionada));
    const maquinasPaginadas = maquinasFiltradas.slice(
        (paginaAtual - 1) * quantidadeSelecionada,
        paginaAtual * quantidadeSelecionada
    );
    // Segurança — se um filtro reduzir o total enquanto o usuário está numa
    // página que deixou de existir, ajusta sozinho em vez de mostrar vazio
    // (mesmo padrão já usado em Ordens de Produção).
    useEffect(() => {
        if (paginaAtual > totalPaginas) setPaginaAtual(totalPaginas);
    }, [totalPaginas]);

    // Mesmo padrão de "voltar ao topo ao trocar de página" implementado em
    // Ordens de Produção — esta tela também não tem container de rolagem
    // próprio (ver MainLayout.jsx).
    const voltarAoTopo = () => window.scrollTo({ top: 0, behavior: 'smooth' });
    const handleMudarBusca = (v) => { setSearchTerm(v); setPaginaAtual(1); };
    const handleMudarFiltroAtivo = (v) => { setFiltroAtivo(v); setPaginaAtual(1); };
    const handleMudarFiltroStatus = (v) => { setFiltroStatus(v); setPaginaAtual(1); };
    const handleMudarQuantidade = (q) => { setQuantidadeSelecionada(q); setPaginaAtual(1); };
    const handlePrimeiraPagina = () => { setPaginaAtual(1); voltarAoTopo(); };
    const handleAnteriorPagina = () => { setPaginaAtual((p) => Math.max(1, p - 1)); voltarAoTopo(); };
    const handleProximaPagina = () => { setPaginaAtual((p) => Math.min(totalPaginas, p + 1)); voltarAoTopo(); };

    return (
        <div className="h-full flex flex-col">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 space-y-4 md:space-y-0">
                <div>
                    <h1 className="text-2xl font-bold text-anthracite-50 tracking-tight flex items-center gap-2.5">
                        <Settings2 size={22} className="text-amber-400" /> Maquinário
                    </h1>
                    <p className="text-[14px] text-anthracite-500 mt-1">Gestão de Equipamentos e Capacidade</p>
                </div>
                <button className="mes-btn-primary"><Plus size={16} /> Cadastrar Máquina</button>
            </div>

            <div className="mes-card-elevated p-5 mb-6 flex flex-col gap-4">
                <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                    <div className="relative w-full md:w-96">
                        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-anthracite-500" />
                        <input type="text" className="mes-input pl-11" placeholder="Buscar por código ou nome..." value={searchTerm} onChange={(e) => handleMudarBusca(e.target.value)} />
                    </div>
                    <div className="flex items-center gap-2">
                        {[{ id: 'ativas', label: 'Ativas' }, { id: 'inativas', label: 'Inativas' }, { id: 'todas', label: 'Todas' }].map((opcao) => (
                            <button
                                key={opcao.id}
                                onClick={() => handleMudarFiltroAtivo(opcao.id)}
                                className={`px-3.5 py-2 rounded-xl text-[12px] font-semibold transition-colors ${filtroAtivo === opcao.id ? 'bg-brand-500/15 text-brand-400 border border-brand-500/30' : 'text-anthracite-500 border border-white/[0.06] hover:bg-white/[0.04]'}`}
                            >
                                {opcao.label}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="flex flex-col md:flex-row gap-4 items-center justify-between border-t border-white/[0.04] pt-4">
                    <div className="flex items-center gap-3">
                        <Filter size={14} className="text-anthracite-500" />
                        <select className="mes-input w-auto" value={filtroStatus} onChange={(e) => handleMudarFiltroStatus(e.target.value)}>
                            <option value="Todos">Todos os Status</option>
                            <option value="Produzindo">Produzindo</option>
                            <option value="Pausada">Pausada</option>
                            <option value="Aguardando">Aguardando</option>
                        </select>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-[12px] text-anthracite-500">Mostrar</span>
                        <div className="flex gap-1 bg-surface-3/50 rounded-xl p-1">
                            {[50, 75, 100].map((q) => (
                                <button key={q} onClick={() => handleMudarQuantidade(q)}
                                    className={`px-3 py-1.5 rounded-lg text-[12px] font-medium transition-colors ${quantidadeSelecionada === q ? 'bg-surface-2 text-brand-400 shadow-sm' : 'text-anthracite-500 hover:text-anthracite-300'}`}>
                                    {q}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {carregando && <div className="text-center py-12 text-anthracite-500">Carregando máquinas...</div>}
            {erroApi && <div className="text-center py-12 text-red-400">{erroApi}</div>}

            {!carregando && !erroApi && maquinasFiltradas.length === 0 && (
                <div className="text-center py-12 text-anthracite-500">Nenhuma máquina encontrada.</div>
            )}

            {!carregando && !erroApi && maquinasFiltradas.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {maquinasPaginadas.map((maquina) => (
                    <div key={maquina.id} className={`mes-card-hover p-6 flex flex-col ${!maquina.ativo ? 'opacity-60' : ''}`}>
                        <div className="mb-4">
                            {/* Header simplificado (13/08/2026) — antes tinha 2 badges
                                competindo (Ativa/Inativa + status) MAIS um botão grande
                                de Ativar/Desativar ocupando a largura toda no meio do
                                card. O estado ativo/inativo já é óbvio pela opacidade
                                do card inteiro, então virou só um ícone pequeno (ação,
                                não mais um badge de status duplicado); o status
                                operacional (Produzindo/Aguardando/etc.) fica sozinho
                                como o único badge, sem disputar atenção. */}
                            <div className="flex justify-between items-center gap-2 mb-2">
                                <div className="text-[10px] font-bold text-anthracite-500 uppercase tracking-wider">{maquina.codigo}</div>
                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                    <span className={`mes-status text-[10px] ${statusBadgeClass[maquina.status] || 'mes-status--muted'}`}>
                                        {statusIcon[maquina.status]}{maquina.status}
                                    </span>
                                    <button
                                        onClick={() => setEditando(maquina)}
                                        title="Editar horas mensais disponíveis"
                                        className="shrink-0 w-6 h-6 rounded-md flex items-center justify-center text-anthracite-500 hover:bg-white/[0.06] hover:text-anthracite-300 transition-colors duration-200"
                                    >
                                        <Pencil size={12} />
                                    </button>
                                    <button
                                        onClick={() => handleToggleAtivo(maquina)}
                                        title={maquina.ativo ? 'Desativar máquina' : 'Ativar máquina'}
                                        className={`shrink-0 w-6 h-6 rounded-md flex items-center justify-center transition-colors duration-200 ${maquina.ativo ? 'text-anthracite-500 hover:bg-red-500/10 hover:text-red-400' : 'text-petrol-400 hover:bg-petrol-500/10'}`}
                                    >
                                        {maquina.ativo ? <PowerOff size={13} /> : <Power size={13} />}
                                    </button>
                                </div>
                            </div>
                            <h3 className="text-[14px] font-bold text-anthracite-100 leading-snug" title={`${maquina.codigo} - ${maquina.nome}`}>{maquina.nome}</h3>
                        </div>

                        <div className="mt-auto space-y-3 pt-4 border-t border-white/[0.04]">
                            <div className="flex justify-between items-center text-[12px]">
                                <span className="text-anthracite-500">Horas/mês:</span>
                                <span className="mes-numeral text-anthracite-200 font-bold">{maquina.horasMensais}</span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            )}

            {!carregando && !erroApi && (
            <div className="mes-card px-6 py-4 mt-6 flex items-center justify-between flex-wrap gap-3">
                {/* Paginação exata (27/08/2026) — diferente de Ordens de
                    Produção, aqui dá pra saber o total de verdade sem custo:
                    a SH1 tem menos de 200 linhas, então a lista inteira já
                    está em memória (ver comentário na declaração de
                    quantidadeSelecionada acima). */}
                <div className="text-[12px] text-anthracite-500">
                    Mostrando <span className="font-medium text-anthracite-300">{maquinasPaginadas.length}</span> de <span className="font-medium text-anthracite-300">{maquinasFiltradas.length}</span> resultados
                </div>
                <div className="flex items-center gap-3">
                    <button onClick={handlePrimeiraPagina} disabled={paginaAtual === 1} title="Voltar para a página inicial"
                        className="mes-btn-ghost p-1.5 disabled:opacity-40 disabled:cursor-not-allowed">
                        <ChevronsLeft size={16} />
                    </button>
                    <button onClick={handleAnteriorPagina} disabled={paginaAtual === 1}
                        className="mes-btn-ghost px-3 py-1.5 text-[12px] disabled:opacity-40 disabled:cursor-not-allowed">
                        Anterior
                    </button>
                    <span className="text-[12px] text-anthracite-500">Página <span className="font-medium text-anthracite-300">{paginaAtual}</span> de <span className="font-medium text-anthracite-300">{totalPaginas}</span></span>
                    <button onClick={handleProximaPagina} disabled={paginaAtual === totalPaginas}
                        className="mes-btn-ghost px-3 py-1.5 text-[12px] disabled:opacity-40 disabled:cursor-not-allowed">
                        Próxima
                    </button>
                </div>
            </div>
            )}

            {editando && (
                <ModalEditarMaquina maquina={editando} onClose={() => setEditando(null)} onSalvar={handleSalvarEdicao} />
            )}
        </div>
    );
};

export default Maquinarios;
